import { computed, Injectable } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { select } from '@app/srv/store.service.js'
import { tr } from '@app/srv/translation.util'
import { AlertInput } from '@ionic/core'
import { localDate } from '@naturalcycles/js-lib'
import {
  Goal,
  Product,
  ProductKey,
  SubscriptionFM,
  SubscriptionStatus,
} from '@naturalcycles/shared'
import { map } from 'rxjs'

@Injectable({ providedIn: 'root' })
export class SubscriptionService {
  private currentSubscription$ = select(s => s.subscription)
  private account = toSignal(select(s => s.account))
  private pregnantNow = toSignal(select(s => s.userFertility.pregnantNow))

  /**
   * Returns true if we consider the user to have a valid subscription. AKA access to the app.
   * We opt for using the `nextPaymentDate` to determine this, as it is simpler that attempting
   * to look at a subscription and all of it details. Use this method to determine if the user
   * is able to perform actions that require a valid subscription.
   */
  public hasOngoingSubscription = computed(() => {
    const account = this.account()
    if (!account?.nextPaymentDate) return false
    return localDate(account.nextPaymentDate).isSameOrAfterToday()
  })

  public showAccountBadge$ = this.currentSubscription$.pipe(
    map(
      currentSubscription =>
        !currentSubscription ||
        currentSubscription.status === SubscriptionStatus.PAST_DUE ||
        this.isSubscriptionExpiring(currentSubscription),
    ),
  )

  public deleteAccountReasons = computed<DeleteAccountReason[]>(() => {
    const account = this.account()
    const pregnantNow = this.pregnantNow()

    let goalDeleteAccountReasons: DeleteAccountReason[] = []
    if (account?.goal === Goal.PREVENT) {
      goalDeleteAccountReasons = this.getPreventDeleteAccountReasons()
    } else if (account?.goal === Goal.PLAN) {
      goalDeleteAccountReasons = this.getPlanDeleteAccountReasons(!!pregnantNow)
    }

    return [
      ...this.getDefaultDeleteAccountReasons(),
      ...goalDeleteAccountReasons,
      ...this.getOtherDeleteAccountReasons(),
    ]
  })

  public isSubscriptionExpiring(currentSubscription: SubscriptionFM | null): boolean {
    if (!currentSubscription) return false

    const expiresInDays = localDate
      .orToday(currentSubscription.endDate)
      .diff(localDate.today(), 'day')
    return !!currentSubscription.endDate && expiresInDays < 10
  }

  public getDurationUnit(subscription: Product): string {
    switch (subscription.key) {
      case ProductKey.MONTH: {
        return tr(`month`)
      }
      case ProductKey.YEAR: {
        return tr(`txt-year`)
      }
      default: {
        return `${subscription.duration}-${subscription.durationUnit}`
      }
    }
  }

  private getDefaultDeleteAccountReasons(): DeleteAccountReason[] {
    return [
      {
        label: tr('cancel-feedback-reason-1'),
        value: 'cancel-feedback-reason-1',
      },
      {
        label: tr('cancel-feedback-reason-2'),
        value: 'cancel-feedback-reason-2',
      },
      {
        label: tr('cancel-feedback-reason-3'),
        value: 'cancel-feedback-reason-3',
      },
    ]
  }

  private getOtherDeleteAccountReasons(): DeleteAccountReason[] {
    return [
      {
        label: tr('cancel-feedback-other'),
        value: 'cancel-feedback-other',
      },
    ]
  }

  private getPreventDeleteAccountReasons(): DeleteAccountReason[] {
    return [
      {
        label: tr('cancel-feedback-prevent-2'),
        value: 'cancel-feedback-prevent-2',
      },
      {
        label: tr('cancel-feedback-prevent-3'),
        value: 'cancel-feedback-prevent-3',
      },
      {
        label: tr('cancel-feedback-prevent'),
        value: 'cancel-feedback-prevent',
      },
    ]
  }

  private getPlanDeleteAccountReasons(pregnantNow: boolean): DeleteAccountReason[] {
    const reason = [
      {
        label: tr('cancel-feedback-plan'),
        value: 'cancel-feedback-plan',
      },
    ]
    if (pregnantNow) {
      reason.push({
        label: tr('cancel-feedback-nonpregnant'),
        value: 'cancel-feedback-nonpregnant',
      })
    }
    return reason
  }
}

type DeleteAccountReason = Pick<AlertInput, 'label' | 'value'>
